// The admin recycle bin — the screen behind the delete confirm's promise.
//
// Every delete in the product says "an admin can get it back for 30 days", and
// until this page that was true only through the API. The purge sweeper
// genuinely deletes at 30 days (attachments/sweeper.ts on the server), so the
// promise now has a door as well as a mechanism.
//
// A deleted document cannot be opened or downloaded from here — the content
// route answers 404 for anything soft-deleted, on purpose. Recognition is
// carried by the words instead: the title, where it came from, who deleted it
// and when. Restore puts it straight back where it was.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { documents, type DeletedAttachment, type OwnerKind } from '../documentsApi';
import { ApiError } from '../api';
import { formatSize } from '../components/Documents';
import { CardList, EmptyState, PageHeader, RowCard, StatusBadge } from '../components/ui';
import { SortHeader } from '../components/ui/SortHeader';
import { sortRows, useSort } from '../lib/sortRows';

type SortKey = 'title' | 'owner' | 'size' | 'deleted';

/** Mirrors RECOVERY_WINDOW_DAYS on the server, and the copy on every delete confirm. */
const RECOVERY_WINDOW_DAYS = 30;

const OWNER_WORD: Record<OwnerKind, string> = {
  job: 'Job',
  resource: 'Resource',
  library: 'Library',
  post: 'Announcement',
  quote: 'Quote',
  timesheet: 'Timesheet',
};

/** Where a restored document lands, so the admin can go and check it did. */
function ownerPath(file: DeletedAttachment): string {
  switch (file.ownerKind) {
    case 'job':
      return `/app/jobs/${file.ownerId}`;
    case 'resource':
      return `/app/resources/${file.ownerId}`;
    case 'library':
      return '/app/library';
    case 'post':
      return `/app/announcements/${file.ownerId}`;
    case 'quote':
      return `/app/quotes/${file.ownerId}`;
    case 'timesheet':
      return `/app/timesheets/${file.ownerId}`;
  }
}

/** Whole days before the sweeper takes it, never below zero. Null when the
    row somehow has no deletion time — render nothing rather than "NaN days". */
function daysLeft(deletedAt: string | null): number | null {
  if (!deletedAt) return null;
  const purgeAt = new Date(deletedAt).getTime() + RECOVERY_WINDOW_DAYS * 86_400_000;
  if (!Number.isFinite(purgeAt)) return null;
  return Math.max(0, Math.ceil((purgeAt - Date.now()) / 86_400_000));
}

function deletedLine(file: DeletedAttachment): string {
  const when = file.deletedAt ? new Date(file.deletedAt).toLocaleDateString('en-AU') : '';
  return file.deletedByName ? `Deleted ${when} by ${file.deletedByName}` : `Deleted ${when}`;
}

export function RecentlyDeletedPage() {
  const [items, setItems] = useState<DeletedAttachment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [restored, setRestored] = useState<DeletedAttachment | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Newest deletion first — the thing somebody has just deleted by mistake is
  // the thing they came here for.
  const { sortKey, sortDir, onSort } = useSort<SortKey>('deleted', 'desc');

  const visible = useMemo(
    () =>
      sortRows(
        items ?? [],
        (file) => {
          switch (sortKey) {
            case 'title':
              return file.title;
            case 'owner':
              return file.ownerName;
            case 'size':
              return file.sizeBytes;
            // Sorting by when it was deleted IS sorting by time left — they
            // are the same order, since everything expires the same number of
            // days after deletion. One sortable column, not two saying the
            // same thing in different words.
            case 'deleted':
              return file.deletedAt ? new Date(file.deletedAt) : null;
          }
        },
        sortDir
      ),
    [items, sortKey, sortDir]
  );

  useEffect(() => {
    void documents
      .deleted()
      .then((result) => setItems(result.attachments))
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : 'Could not load the deleted documents.')
      );
  }, []);

  async function restore(file: DeletedAttachment) {
    setBusyId(file.id);
    setError(null);
    try {
      await documents.restore(file.id);
      setItems((current) => (current ?? []).filter((f) => f.id !== file.id));
      setRestored(file);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not restore that document.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Recently deleted"
        subtitle={`Documents someone removed in the last ${RECOVERY_WINDOW_DAYS} days. Restoring one puts it straight back where it was; after ${RECOVERY_WINDOW_DAYS} days it is gone for good.`}
      />

      {error && (
        <div className="banner-error mb-4" role="alert">
          {error}
        </div>
      )}

      {restored && (
        <div className="banner-ok mb-4" role="status">
          Restored &ldquo;{restored.title}&rdquo; back to {OWNER_WORD[restored.ownerKind].toLowerCase()}{' '}
          {restored.ownerName}.{' '}
          <Link to={ownerPath(restored)} className="font-medium underline">
            Go and see it
          </Link>
        </div>
      )}

      {items === null && !error ? (
        <div className="card px-5 py-8 text-center text-sm text-gray-500">Loading…</div>
      ) : items !== null && items.length === 0 ? (
        <EmptyState
          title="Nothing waiting here"
          body={`When someone removes a document it sits here for ${RECOVERY_WINDOW_DAYS} days before it is deleted for good, so a slip of the finger is never fatal.`}
        />
      ) : items !== null ? (
        <>
          <CardList>
            {visible.map((file) => (
              <RowCard
                key={file.id}
                title={file.title}
                badges={<TimeLeftBadge days={daysLeft(file.deletedAt)} />}
                actions={
                  <button
                    type="button"
                    className="row-action"
                    disabled={busyId === file.id}
                    onClick={() => void restore(file)}
                  >
                    {busyId === file.id ? 'Restoring…' : 'Restore'}
                  </button>
                }
              >
                <div>
                  {OWNER_WORD[file.ownerKind]}:{' '}
                  <Link to={ownerPath(file)} className="text-brand-800 underline">
                    {file.ownerName}
                  </Link>
                </div>
                <div>
                  {formatSize(file.sizeBytes)} · {deletedLine(file)}
                </div>
              </RowCard>
            ))}
          </CardList>

          {/* The desktop half of the standard card/table pair (phase 8, DM3). */}
          <div className="table-wrap max-sm:hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <SortHeader label="Document" sortKey="title" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Was on" sortKey="owner" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader
                    label="Size"
                    sortKey="size"
                    current={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    className="table-num"
                  />
                  <SortHeader label="Deleted" sortKey="deleted" current={sortKey} dir={sortDir} onSort={onSort} />
                  {/* Not sortable, and not an oversight: time left is the
                      deletion date counted forward, so it sorts by the column
                      beside it. Two headings for one order would just be two
                      ways to ask the same question. */}
                  <th scope="col" className="table-header">
                    Time left
                  </th>
                  <th className="w-px" />
                </tr>
              </thead>
              <tbody>
                {visible.map((file) => (
                  <tr key={file.id}>
                    <td className="font-medium text-gray-900">{file.title}</td>
                    <td>
                      <Link to={ownerPath(file)} className="table-link">
                        {file.ownerName}
                      </Link>
                      <div className="mt-0.5 text-xs text-gray-600">
                        {OWNER_WORD[file.ownerKind]}
                      </div>
                    </td>
                    <td className="table-muted table-num whitespace-nowrap">
                      {formatSize(file.sizeBytes)}
                    </td>
                    <td className="table-muted">{deletedLine(file)}</td>
                    <td>
                      <TimeLeftBadge days={daysLeft(file.deletedAt)} />
                    </td>
                    <td className="whitespace-nowrap text-right">
                      <button
                        type="button"
                        className="row-action"
                        disabled={busyId === file.id}
                        onClick={() => void restore(file)}
                      >
                        {busyId === file.id ? 'Restoring…' : 'Restore'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}

/** Amber inside the last week — the same "running out" vocabulary as expiry. */
function TimeLeftBadge({ days }: { days: number | null }) {
  if (days === null) return null;
  return (
    <StatusBadge
      kind="expiry"
      status={days <= 7 ? 'soon' : 'ok'}
      label={days === 1 ? '1 day left' : `${days} days left`}
    />
  );
}
